import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatCurrency,
  formatPct,
} from './shared';

interface Segment {
  segment: string;
  customers: number;
  pct_of_total: number;
  avg_ltv: number;
  avg_orders: number;
  revenue_30d: number;
  reachable_email?: number;
  reachable_line?: number;
}

interface SegmentsData {
  total_customers: number;
  segments: Segment[];
  overlays: {
    high_value: number;
    high_value_avg_ltv: number;
    whale: number;
  };
}

const SEGMENT_META: Record<
  string,
  { label: string; color: string; action: string; emoji: string }
> = {
  active_vip: {
    label: '活躍 VIP',
    color: COLORS.vip,
    action: '持續培養、獨家商品',
    emoji: '★',
  },
  new: {
    label: '新客戶（30 天內）',
    color: COLORS.success,
    action: 'Day 5 / 10 / 21 歡迎流程',
    emoji: '+',
  },
  recent: {
    label: '近期客戶（31-60 天）',
    color: COLORS.primary,
    action: '新品通知、搭配建議',
    emoji: '◆',
  },
  at_risk: {
    label: '風險客戶（61-90 天）',
    color: COLORS.warning,
    action: 'Day 60 召回訊息 + 優惠',
    emoji: '!',
  },
  lost: {
    label: '流失客戶（91-180 天）',
    color: '#EA580C',
    action: 'Day 90 召回 + 10% off',
    emoji: '×',
  },
  deep_lost: {
    label: '深度流失（180 天以上）',
    color: COLORS.danger,
    action: '年度回歸活動',
    emoji: '✕',
  },
};

const SEGMENT_ORDER = ['active_vip', 'new', 'recent', 'at_risk', 'lost', 'deep_lost'];

const SegmentsSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SegmentsData | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get('/api/retention/segments')
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!data) return <SectionError />;

  // Sort by defined order
  const sorted = [...data.segments].sort(
    (a, b) => SEGMENT_ORDER.indexOf(a.segment) - SEGMENT_ORDER.indexOf(b.segment)
  );

  const pieData = sorted.map((s) => ({
    name: SEGMENT_META[s.segment]?.label || s.segment,
    value: s.customers,
    color: SEGMENT_META[s.segment]?.color || COLORS.slate,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Overlays */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="總客戶數"
            value={formatNumber(data.total_customers)}
            subtitle="有購買紀錄"
            color={COLORS.primary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="高價值客戶"
            value={formatNumber(data.overlays.high_value)}
            subtitle={`LTV > NT$5K（平均 ${formatCurrency(data.overlays.high_value_avg_ltv)}）`}
            color={COLORS.warning}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="鯨魚客戶"
            value={formatNumber(data.overlays.whale)}
            subtitle="LTV > NT$10K — 個人化服務"
            color={COLORS.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="可召回客戶"
            value={formatNumber(
              sorted
                .filter((s) => ['at_risk', 'lost', 'deep_lost'].includes(s.segment))
                .reduce((acc, s) => acc + s.customers, 0)
            )}
            subtitle="風險 + 流失 + 深度流失"
            color={COLORS.danger}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Pie chart */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="客戶分佈"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={2}
                    dataKey="value"
                    label={(p) => `${p.name}: ${p.value}`}
                    labelLine={false}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatNumber(v as number)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Segment table */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="分層與建議行動"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>分層</TableCell>
                      <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                        人數
                      </TableCell>
                      <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                        佔比
                      </TableCell>
                      <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                        平均 LTV
                      </TableCell>
                      <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                        30 天營收
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                        可聯繫
                      </TableCell>
                      <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>行動</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((s) => {
                      const meta = SEGMENT_META[s.segment];
                      return (
                        <TableRow key={s.segment} hover>
                          <TableCell>
                            <Chip
                              size="small"
                              label={meta?.label || s.segment}
                              sx={{
                                bgcolor: `${meta?.color || COLORS.slate}22`,
                                color: meta?.color || COLORS.slate,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(s.customers)}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(s.pct_of_total, 100)}
                                sx={{
                                  width: 40,
                                  height: 4,
                                  borderRadius: 2,
                                  bgcolor: '#E2E8F0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: meta?.color || COLORS.slate,
                                  },
                                }}
                              />
                              <Typography
                                sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', minWidth: 42 }}
                              >
                                {formatPct(s.pct_of_total)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(s.avg_ltv)}</TableCell>
                          <TableCell align="right">{formatCurrency(s.revenue_30d)}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={`✉ ${formatNumber(s.reachable_email ?? 0)}`}
                                title={`Email 可聯繫: ${s.reachable_email ?? 0} / ${s.customers}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.68rem',
                                  bgcolor: (s.reachable_email ?? 0) > 0 ? `${COLORS.primary}18` : '#F1F5F9',
                                  color: (s.reachable_email ?? 0) > 0 ? COLORS.primary : COLORS.slate,
                                  fontWeight: 600,
                                }}
                              />
                              <Chip
                                size="small"
                                label={`L ${formatNumber(s.reachable_line ?? 0)}`}
                                title={`LINE 已綁定: ${s.reachable_line ?? 0} / ${s.customers}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.68rem',
                                  bgcolor: (s.reachable_line ?? 0) > 0 ? '#06C75518' : '#F1F5F9',
                                  color: (s.reachable_line ?? 0) > 0 ? '#06C755' : COLORS.slate,
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {meta?.action || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SegmentsSection;
